import { useMemo, useState } from 'react';
import { Code, RefreshCw, Trash2 } from 'lucide-react';
import { DropdownMenuItem, DropdownMenuSeparator } from '@loader/ui';
import type { TableConnector, ConnectorType } from '@loader/shared-types';

import { CONNECTOR_META } from '../types';
import { useUpdateCustomSchedule } from '../hooks/useCustomConnectors';
import { useDeleteConnector } from '../hooks/useBaseConnectors';
import { useSyncTrigger, useConnectorToggle, useSyncProgress } from '../hooks/useSyncTrigger';
import { toSyncStatus, resolveEffectiveStatus, IntegrationCard, ConnectorSyncRow, ScheduleMenuItem } from './shared';
import type { SyncStatus } from './shared';
import { PostApiInstructionsDialog } from './PostApiInstructionsDialog';
import { useScheduleEditDialog } from './useScheduleEditDialog';

import { ConfirmDeleteDialog } from '@/components/common';
import { useToastStore } from '@/store';
import type { Table } from '@/lib/types';

interface ConnectorTypeGroupProps {
  type: ConnectorType;
  connectors: TableConnector[];
  tables: Table[];
}

export function ConnectorTypeGroup({ type, connectors, tables }: ConnectorTypeGroupProps) {
  const meta = CONNECTOR_META[type];
  const Icon = meta?.icon;
  const toast = useToastStore();
  const updateSchedule = useUpdateCustomSchedule();
  const deleteConnector = useDeleteConnector();
  const { handleTriggerSync, syncingTableIds } = useSyncTrigger();
  const { handlePause, handleResume } = useConnectorToggle();
  const [instructionsTarget, setInstructionsTarget] = useState<{ tableId: string; tableName: string } | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<{ tableId: string; tableName: string } | null>(null);
  const { openScheduleDialog, scheduleEditDialog } = useScheduleEditDialog(updateSchedule);

  const handleSyncAll = () => {
    for (const conn of connectors) {
      if (conn.enabled) handleTriggerSync(conn.table_id);
    }
  };

  // Sort connectors by table name for consistent ordering
  const sortedConnectors = useMemo(() => {
    const tableNameMap = new Map(tables.map((t) => [t.id, t.name ?? '']));
    return [...connectors].sort((a, b) => {
      const aName = tableNameMap.get(a.table_id) ?? '';
      const bName = tableNameMap.get(b.table_id) ?? '';
      return aName.localeCompare(bName);
    });
  }, [connectors, tables]);

  // Pull connectors (http_fetch, sql) support "Sync All"
  const isPullType = type === 'http_fetch' || type === 'sql';

  return (
    <>
      <IntegrationCard
        icon={Icon ? <Icon className="text-muted-foreground h-5 w-5 shrink-0" /> : null}
        label={meta?.label ?? type}
        subtitle={`${connectors.length} ${connectors.length === 1 ? 'table' : 'tables'}`}
        headerMenuItems={isPullType && connectors.length > 1 ? (
          <DropdownMenuItem onClick={handleSyncAll}>
            <RefreshCw className="mr-2 h-3.5 w-3.5" />
            Sync All
          </DropdownMenuItem>
        ) : undefined}
      >
        {sortedConnectors.map((conn) => {
          const table = tables.find((t) => t.id === conn.table_id);
          const isSyncing = syncingTableIds.has(conn.table_id);
          const status = toSyncStatus({ enabled: conn.enabled, isSyncing, lastSyncedAt: conn.last_synced_at });

          return (
            <CustomSyncRowWithProgress
              key={conn.id}
              connector={conn}
              name={table?.name ?? 'Unknown table'}
              status={status}
              isPullType={isPullType}
              isPostApi={type === 'post_api'}
              onTriggerSync={handleTriggerSync}
              onPause={handlePause}
              onResume={handleResume}
              onOpenSchedule={() => openScheduleDialog(conn.table_id, conn.schedule ?? null)}
              onShowInstructions={() => setInstructionsTarget({
                tableId: conn.table_id,
                tableName: table?.name ?? 'Unknown table',
              })}
              onDelete={() => setDeleteTarget({
                tableId: conn.table_id,
                tableName: table?.name ?? 'Unknown table',
              })}
            />
          );
        })}
      </IntegrationCard>

      {instructionsTarget && (
        <PostApiInstructionsDialog
          open
          onOpenChange={(open) => { if (!open) setInstructionsTarget(null); }}
          tableId={instructionsTarget.tableId}
          tableName={instructionsTarget.tableName}
        />
      )}

      {scheduleEditDialog}

      <ConfirmDeleteDialog
        open={!!deleteTarget}
        onOpenChange={(open) => { if (!open) setDeleteTarget(null); }}
        title={`Delete "${deleteTarget?.tableName}"`}
        warningMessage="This connector and its table will be permanently deleted."
        isLoading={deleteConnector.isPending}
        onConfirm={() => {
          if (!deleteTarget) return;
          deleteConnector.mutate(deleteTarget.tableId, {
            onSuccess: () => {
              toast.success('Connector deleted');
              setDeleteTarget(null);
            },
            onError: (error: Error) => toast.error(error.message),
          });
        }}
      />
    </>
  );
}

// ============================================================================
// Wrapper — polls backend sync progress per row (matches ConnectionGroup pattern)
// ============================================================================

function CustomSyncRowWithProgress({
  connector,
  name,
  status,
  isPullType,
  isPostApi,
  onTriggerSync,
  onPause,
  onResume,
  onOpenSchedule,
  onShowInstructions,
  onDelete,
}: {
  connector: TableConnector;
  name: string;
  status: SyncStatus;
  isPullType: boolean;
  isPostApi: boolean;
  onTriggerSync: (tableId: string) => void;
  onPause: (tableId: string) => void;
  onResume: (tableId: string) => void;
  onOpenSchedule: () => void;
  onShowInstructions: () => void;
  onDelete: () => void;
}) {
  const { syncing: backendSyncing, progress } = useSyncProgress(connector.table_id, status === 'syncing');

  const effectiveStatus = resolveEffectiveStatus(status, backendSyncing);

  return (
    <ConnectorSyncRow
      name={name}
      tableId={connector.table_id}
      status={effectiveStatus}
      schedule={connector.schedule}
      lastSyncedAt={connector.last_synced_at}
      progress={progress}
      onTriggerSync={() => onTriggerSync(connector.table_id)}
      onPause={() => onPause(connector.table_id)}
      onResume={() => onResume(connector.table_id)}
      hideScheduleActions={isPostApi}
      extraMenuItems={
        <>
          {isPullType && (
            <ScheduleMenuItem
              schedule={connector.schedule}
              onClick={onOpenSchedule}
            />
          )}
          {isPostApi && (
            <DropdownMenuItem onClick={onShowInstructions}>
              <Code className="mr-2 h-3.5 w-3.5" />
              Instructions
            </DropdownMenuItem>
          )}
          {(isPullType || isPostApi) && <DropdownMenuSeparator />}
          <DropdownMenuItem className="text-destructive" onClick={onDelete}>
            <Trash2 className="mr-2 h-3.5 w-3.5" />
            Delete
          </DropdownMenuItem>
        </>
      }
    />
  );
}
